import React, { useState, useMemo } from 'react';

/*
  PnLHeatmap — Senior Frontend Rewrite
  ──────────────────────────────────────
  - CSS grid: 12 equal-width month columns, fills container 100%
  - All no-trade cells (past, future, weekends) look identical
  - Year filter dropdown
  - Click month pill or month label → detail panel
  - Data: dailyPnl = [{ date:"YYYY-MM-DD", daily:number, trades:number }]
*/

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL  = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DOW_MINI    = ['S','M','T','W','T','F','S'];

/* ── color scale ── */
const PROFIT = ['#1c3f28','#236634','#2a8842','#32aa50','#3acc60'];
const LOSS   = ['#3f1c1c','#661c1c','#882a2a','#aa3232','#cc3c3c'];
const EMPTY  = '#0d1825';   /* same for ALL no-trade cells */
const BORD   = '#141e2d';

function pnlColor(pnl, maxAbs) {
  if (!pnl || isNaN(pnl) || pnl === 0) return EMPTY;
  const ratio = Math.min(Math.abs(pnl) / Math.max(maxAbs, 1), 1);
  const idx   = Math.min(Math.floor(ratio * 5), 4);
  return pnl > 0 ? PROFIT[idx] : LOSS[idx];
}

/* ── helpers ── */
const toKey = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const todayKey = () => toKey(new Date());

const fmtINR = v => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const abs = Math.abs(v);
  const s   = abs >= 100000 ? (abs/100000).toFixed(1)+'L'
            : abs >= 1000   ? (abs/1000).toFixed(1)+'k'
            : Math.round(abs)+'';
  return (v < 0 ? '-' : '+') + '₹' + s;
};

const fmtShortDate = s => {
  try { return new Date(s+'T00:00:00').toLocaleDateString('en-IN',
    { weekday:'short', day:'numeric', month:'short' }); }
  catch { return s; }
};

const fmtLongDate = s => {
  try { return new Date(s+'T00:00:00').toLocaleDateString('en-IN',
    { weekday:'long', day:'numeric', month:'long', year:'numeric' }); }
  catch { return s; }
};

/* ── build pnlMap from dailyPnl ── */
function buildMap(dailyPnl) {
  const map = {};
  dailyPnl.forEach(d => {
    if (!d.date) return;
    map[d.date] = { pnl: d.daily ?? d.pnl ?? 0, trades: d.trades ?? 0 };
  });
  return map;
}

/* ── build one month's cell grid ── */
function buildMonth(year, month, pnlMap) {
  const today    = todayKey();
  const dim      = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const cells    = [];

  /* leading blanks */
  for (let i = 0; i < firstDow; i++) cells.push(null);

  for (let d = 1; d <= dim; d++) {
    const key    = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    const data   = pnlMap[key];
    const future = key > today;
    /* a cell "has data" only if it's not future AND trades > 0 */
    const hasData = !!(data && data.trades > 0 && !future);
    cells.push({
      date: key, day: d, month, year,
      pnl:     hasData ? (data?.pnl ?? 0) : null,
      trades:  hasData ? (data?.trades ?? 0) : 0,
      hasData,
      isFuture: future,
      isToday:  key === today,
    });
  }
  /* trailing blanks to complete last week */
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/* ── global stats from pnlMap ── */
function computeStats(dailyPnl, year) {
  const today  = todayKey();
  const vals   = dailyPnl
    .filter(d => d.date?.startsWith(String(year)) && d.date <= today && (d.trades??0) > 0)
    .map(d => d.daily ?? d.pnl ?? 0)
    .filter(v => !isNaN(v));

  const total  = vals.reduce((s,v) => s+v, 0);
  const win    = vals.filter(v=>v>0).length;
  const loss   = vals.filter(v=>v<0).length;
  const wr     = vals.length ? Math.round(win/vals.length*100) : 0;
  const best   = vals.length ? Math.max(...vals) : 0;
  const worst  = vals.length ? Math.min(...vals) : 0;

  /* current win streak from latest day backward */
  const sorted = dailyPnl
    .filter(d => d.date?.startsWith(String(year)) && d.date <= today && (d.trades??0) > 0)
    .sort((a,b) => a.date.localeCompare(b.date));
  let streak = 0;
  for (let i = sorted.length-1; i>=0; i--) {
    if ((sorted[i].daily ?? sorted[i].pnl ?? 0) > 0) streak++;
    else break;
  }

  return { total, win, loss, wr, best, worst, streak, count: vals.length };
}

/* ══════════════════════════════════════════════
   TOOLTIP
══════════════════════════════════════════════ */
function Tooltip({ cell, x, y }) {
  if (!cell) return null;
  const pos = (cell.pnl ?? 0) > 0, neg = (cell.pnl ?? 0) < 0;
  return (
    <div style={{
      position:'fixed', left:x+14, top:y-14, zIndex:99999,
      background:'#0a1628', border:'1px solid #2a3f5f', borderRadius:12,
      padding:'12px 16px', fontFamily:"'DM Mono',monospace",
      pointerEvents:'none', minWidth:170, lineHeight:1.6,
      boxShadow:'0 8px 32px rgba(0,0,0,0.6)',
    }}>
      <div style={{ fontSize:10, color:'#5a6a82', marginBottom:8 }}>
        {fmtLongDate(cell.date)}
      </div>
      {cell.hasData ? (
        <>
          <div style={{ fontSize:20, fontWeight:700, marginBottom:3,
            color:pos?'#3acc60':neg?'#cc3c3c':'#8899b0' }}>
            {fmtINR(cell.pnl)}
          </div>
          <div style={{ fontSize:11, color:'#5a6a82' }}>
            {cell.trades} trade{cell.trades!==1?'s':''} · {pos?'Profit':'Loss'} day
          </div>
        </>
      ) : (
        <div style={{ fontSize:12, color:'#3a4a5a' }}>No trades</div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MONTH DETAIL PANEL
══════════════════════════════════════════════ */
function MonthDetail({ month, year, cells, maxAbs, onClose }) {
  const tradeDays = cells.filter(c => c?.hasData);
  const totalPnl  = tradeDays.reduce((s,c) => s+(c.pnl??0), 0);
  const wins      = tradeDays.filter(c=>(c.pnl??0)>0);
  const losses    = tradeDays.filter(c=>(c.pnl??0)<0);

  return (
    <div style={{
      marginTop:16, background:'#0c1423',
      border:'1px solid #2a3f5f', borderRadius:14,
      padding:'20px 22px', position:'relative',
    }}>
      <button onClick={onClose} style={{
        position:'absolute', top:14, right:14, background:'transparent',
        border:'1px solid #1e2d45', borderRadius:6, color:'#5a6a82',
        width:26, height:26, cursor:'pointer', fontSize:14,
        display:'flex', alignItems:'center', justifyContent:'center', padding:0,
        fontFamily:'monospace',
      }}>✕</button>

      {/* header */}
      <div style={{ display:'flex', alignItems:'baseline', gap:16, marginBottom:18, flexWrap:'wrap' }}>
        <div style={{ fontSize:17, fontWeight:700, color:'#fff', fontFamily:"'Outfit',sans-serif" }}>
          {MONTH_FULL[month]} {year}
        </div>
        {tradeDays.length > 0 ? (
          <div style={{ display:'flex', gap:14, fontSize:12, fontFamily:"'DM Mono',monospace" }}>
            <span style={{ color:totalPnl>=0?'#3acc60':'#cc3c3c', fontWeight:700 }}>{fmtINR(totalPnl)}</span>
            <span style={{ color:'#3acc60' }}>{wins.length} profit</span>
            <span style={{ color:'#cc3c3c' }}>{losses.length} loss</span>
            <span style={{ color:'#5a6a82' }}>{tradeDays.length} trading days</span>
          </div>
        ) : (
          <span style={{ fontSize:12, color:'#3a4a5a', fontFamily:"'DM Mono',monospace" }}>No trades this month</span>
        )}
      </div>

      <div className="pnl-detail-grid" style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:28 }}>

        {/* mini calendar */}
        <div style={{ minWidth:200 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,28px)', gap:3, marginBottom:4 }}>
            {DOW_MINI.map((h,i) => (
              <div key={i} style={{ fontSize:9, color:'#3a4a5a', textAlign:'center',
                fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{h}</div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,28px)', gap:3 }}>
            {cells.map((cell, i) => {
              if (!cell) return <div key={i} style={{ width:28, height:28 }} />;
              const bg     = cell.hasData ? (pnlColor(cell.pnl, maxAbs) ?? EMPTY) : EMPTY;
              const hasClr = cell.hasData && cell.pnl !== 0;
              return (
                <div key={cell.date} style={{
                  width:28, height:28, borderRadius:5,
                  background:bg,
                  border:`1px solid ${cell.isToday?'#3acc60':hasClr?'transparent':BORD}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  position:'relative',
                  opacity: cell.isFuture ? 0.2 : 1,
                }}>
                  <span style={{
                    fontSize:9, fontFamily:"'DM Mono',monospace",
                    color:hasClr?'rgba(255,255,255,0.75)':'#2a3a4a',
                  }}>{cell.day}</span>
                  {cell.hasData && (
                    <div style={{ position:'absolute', bottom:2, right:2,
                      width:4, height:4, borderRadius:'50%',
                      background:cell.pnl>0?'#7fffb0':'#ff9090' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* trade list */}
        <div>
          <div style={{ fontSize:10, color:'#5a6a82', fontFamily:"'DM Mono',monospace",
            letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:10 }}>
            Trade Days
          </div>
          {tradeDays.length === 0 ? (
            <div style={{ color:'#3a4a5a', fontSize:13, fontFamily:"'DM Mono',monospace", padding:'24px 0' }}>
              No trades recorded this month
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:260, overflowY:'auto' }}>
              {tradeDays.sort((a,b)=>a.date.localeCompare(b.date)).map(c => {
                const pos=(c.pnl??0)>0, neg=(c.pnl??0)<0;
                return (
                  <div key={c.date} style={{
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    background:'#0d1825', border:'1px solid #1a2a3d',
                    borderRadius:8, padding:'10px 14px',
                  }}>
                    <div>
                      <div style={{ fontSize:13, color:'#e8edf5', fontFamily:"'DM Mono',monospace", marginBottom:2 }}>
                        {fmtShortDate(c.date)}
                      </div>
                      <div style={{ fontSize:10, color:'#5a6a82', fontFamily:"'DM Mono',monospace" }}>
                        {c.trades} trade{c.trades!==1?'s':''}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:16, fontWeight:700, fontFamily:"'DM Mono',monospace",
                        color:pos?'#3acc60':neg?'#cc3c3c':'#8899b0' }}>
                        {fmtINR(c.pnl)}
                      </div>
                      <div style={{ fontSize:9, fontFamily:"'DM Mono',monospace",
                        textTransform:'uppercase', marginTop:2,
                        color:pos?'#3acc60':neg?'#cc3c3c':'#5a6a82' }}>
                        {pos?'Profit':neg?'Loss':'Flat'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
export default function PnLHeatmap({ dailyPnl = [], title = 'P&L Calendar' }) {
  const currentYear = new Date().getFullYear();

  /* derive available years from data + current year */
  const availableYears = useMemo(() => {
    const years = new Set([currentYear]);
    dailyPnl.forEach(d => { if (d.date) years.add(parseInt(d.date.slice(0,4))); });
    return [...years].sort((a,b) => b-a);   /* newest first */
  }, [dailyPnl, currentYear]);

  const [year,          setYear]          = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [hovered,       setHovered]       = useState(null);
  const [mx,            setMx]            = useState(0);
  const [my,            setMy]            = useState(0);

  const pnlMap = useMemo(() => buildMap(dailyPnl), [dailyPnl]);

  /* build all 12 months for selected year */
  const months = useMemo(() =>
    Array.from({ length:12 }, (_,m) => ({
      month: m,
      cells: buildMonth(year, m, pnlMap),
    })),
  [year, pnlMap]);

  const stats  = useMemo(() => computeStats(dailyPnl, year), [dailyPnl, year]);
  const maxAbs = useMemo(() => {
    const today = todayKey();
    const vals  = dailyPnl
      .filter(d => d.date?.startsWith(String(year)) && d.date <= today && (d.trades??0) > 0)
      .map(d => Math.abs(d.daily ?? d.pnl ?? 0))
      .filter(v => v > 0 && !isNaN(v));
    return vals.length ? Math.max(...vals) : 1;
  }, [dailyPnl, year]);

  const selectedCells = selectedMonth !== null ? months[selectedMonth]?.cells : null;

  /* close detail when year changes */
  const handleYearChange = (y) => { setYear(parseInt(y)); setSelectedMonth(null); };

  return (
    <div
      className="pnl-heatmap"
      style={{ background:'#060d1a', border:'1px solid #1a2a3d', borderRadius:16, padding:'20px 22px 20px' }}
      onMouseMove={e => { if(hovered){ setMx(e.clientX); setMy(e.clientY); } }}
    >
      <style>{`
        @media (max-width: 900px) {
          .pnl-year-grid { grid-template-columns: repeat(6, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .pnl-heatmap   { padding: 14px 14px !important; }
          .pnl-year-grid { grid-template-columns: repeat(4, 1fr) !important; }
          .pnl-detail-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .pnl-stat-pills { gap: 5px !important; }
          .pnl-stat-pills > div { min-width: 60px !important; padding: 5px 8px !important; }
        }
        @media (max-width: 400px) {
          .pnl-year-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
      `}</style>

      {/* ── Stat bar ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:16 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'#fff', fontFamily:"'Outfit',sans-serif", marginBottom:3 }}>
            {title}
          </div>
          <div style={{ fontSize:11, color:'#5a6a82', fontFamily:"'DM Mono',monospace" }}>
            {stats.count} trading days
          </div>
        </div>
        <div className="pnl-stat-pills" style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>

          {/* Year selector */}
          <select
            value={year}
            onChange={e => handleYearChange(e.target.value)}
            style={{
              background:'#111827', border:'1px solid #1e2d45', borderRadius:10,
              color:'#8899b0', fontSize:13, fontFamily:"'DM Mono',monospace",
              padding:'7px 12px', cursor:'pointer', outline:'none',
            }}
          >
            {availableYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {/* Stat pills */}
          {[
            { label:'Net PnL',   value:fmtINR(stats.total), color:stats.total>=0?'#3acc60':'#cc3c3c', bg:stats.total>=0?'rgba(58,204,96,0.08)':'rgba(204,60,60,0.08)', bd:stats.total>=0?'rgba(58,204,96,0.25)':'rgba(204,60,60,0.25)' },
            { label:'Win Rate',  value:stats.wr+'%',          color:stats.wr>=50?'#3acc60':'#cc3c3c',   bg:'rgba(255,255,255,0.03)', bd:'#1e2d45' },
            { label:'Best Day',  value:fmtINR(stats.best),   color:'#3acc60', bg:'rgba(58,204,96,0.06)',  bd:'rgba(58,204,96,0.2)' },
            { label:'Worst Day', value:fmtINR(stats.worst),  color:'#cc3c3c', bg:'rgba(204,60,60,0.06)',  bd:'rgba(204,60,60,0.2)' },
            { label:'Streak',    value:stats.streak+'d 🔥',  color:'#f5a623', bg:'rgba(245,166,35,0.06)', bd:'rgba(245,166,35,0.2)' },
          ].map(({ label, value, color, bg, bd }) => (
            <div key={label} style={{ background:bg, border:`1px solid ${bd}`, borderRadius:10, padding:'7px 14px', textAlign:'center', minWidth:78 }}>
              <div style={{ fontSize:9, color:'#5a6a82', fontFamily:"'DM Mono',monospace", letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:4 }}>
                {label}
              </div>
              <div style={{ fontSize:15, fontWeight:700, color, fontFamily:"'DM Mono',monospace" }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Month pills ── */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
        <button
          onClick={() => setSelectedMonth(null)}
          style={{
            padding:'4px 12px', borderRadius:20, fontSize:11, cursor:'pointer',
            fontFamily:"'DM Mono',monospace", fontWeight:600, transition:'all 0.15s',
            background: selectedMonth===null ? '#3acc60' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${selectedMonth===null ? '#3acc60' : '#1e2d45'}`,
            color: selectedMonth===null ? '#060d1a' : '#8899b0',
          }}
        >All</button>

        {MONTH_SHORT.map((m, i) => {
          const active = selectedMonth === i;
          return (
            <button key={m} onClick={() => setSelectedMonth(active ? null : i)}
              style={{
                padding:'4px 12px', borderRadius:20, fontSize:11, cursor:'pointer',
                fontFamily:"'DM Mono',monospace", fontWeight:600, transition:'all 0.15s',
                background: active ? '#3acc60' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${active ? '#3acc60' : '#1e2d45'}`,
                color: active ? '#060d1a' : '#8899b0',
              }}
            >{m}</button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════
          FULL YEAR GRID — 12 equal columns
          Each column = 1 month
      ══════════════════════════════════════ */}
      <div className="pnl-year-grid" style={{
        display:'grid',
        gridTemplateColumns:'repeat(12, 1fr)',
        gap:6,
        width:'100%',
      }}>
        {months.map(({ month, cells }) => {
          const isSelected = selectedMonth === month;
          const isFiltered = selectedMonth !== null && !isSelected;

          /* per-month stats for label */
          const mTrades = cells.filter(c=>c?.hasData);
          const mPnl    = mTrades.reduce((s,c)=>s+(c.pnl??0),0);

          return (
            <div
              key={month}
              onClick={() => setSelectedMonth(isSelected ? null : month)}
              style={{
                cursor:'pointer',
                opacity: isFiltered ? 0.35 : 1,
                transition:'opacity 0.2s',
              }}
            >
              {/* month label */}
              <div style={{
                fontSize:10, fontWeight:700,
                color: isSelected ? '#3acc60' : '#5a6a82',
                fontFamily:"'DM Mono',monospace",
                marginBottom:4, textAlign:'center',
                transition:'color 0.15s',
              }}>
                {MONTH_SHORT[month]}
                {mTrades.length > 0 && (
                  <span style={{
                    marginLeft:3,
                    color:mPnl>=0?'#3acc60':'#cc3c3c',
                    fontSize:8,
                  }}>
                    {fmtINR(mPnl)}
                  </span>
                )}
              </div>

              {/* day-of-week mini header */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1, marginBottom:2 }}>
                {DOW_MINI.map((h,i) => (
                  <div key={i} style={{
                    fontSize:7, color:'#2a3a4a', textAlign:'center',
                    fontFamily:"'DM Mono',monospace", lineHeight:1.4,
                  }}>{h}</div>
                ))}
              </div>

              {/* day cells */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1 }}>
                {cells.map((cell, ci) => {
                  if (!cell) return (
                    <div key={ci} style={{ aspectRatio:'1' }} />
                  );

                  /* ALL no-trade cells — past, future, weekends — same color */
                  const bg     = cell.hasData ? (pnlColor(cell.pnl, maxAbs) ?? EMPTY) : EMPTY;
                  const hasClr = cell.hasData && cell.pnl !== 0;

                  return (
                    <div
                      key={cell.date}
                      style={{
                        aspectRatio:'1',
                        borderRadius:2,
                        background:bg,
                        border:`0.5px solid ${cell.isToday?'#3acc60':hasClr?'transparent':BORD}`,
                        opacity: cell.isFuture ? 0.3 : 1,
                        transition:'transform 0.1s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.transform='scale(1.4)';
                        e.currentTarget.style.zIndex='10';
                        setHovered(cell); setMx(e.clientX); setMy(e.clientY);
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform='scale(1)';
                        e.currentTarget.style.zIndex='auto';
                        setHovered(null);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Legend + counts ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:14, flexWrap:'wrap', gap:8 }}>
        <div style={{ fontSize:11, color:'#5a6a82', fontFamily:"'DM Mono',monospace" }}>
          <span style={{ color:'#3acc60' }}>{stats.win} profit</span>
          {' · '}
          <span style={{ color:'#cc3c3c' }}>{stats.loss} loss</span>
          {' · '}
          <span>{stats.count - stats.win - stats.loss} flat</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <span style={{ fontSize:10, color:'#5a6a82', fontFamily:"'DM Mono',monospace" }}>Loss</span>
          {[...LOSS].reverse().map((c,i) => (
            <div key={i} style={{ width:12, height:12, background:c, borderRadius:3 }} />
          ))}
          <div style={{ width:12, height:12, background:EMPTY, borderRadius:3, border:`0.5px solid ${BORD}` }} />
          {PROFIT.map((c,i) => (
            <div key={i} style={{ width:12, height:12, background:c, borderRadius:3 }} />
          ))}
          <span style={{ fontSize:10, color:'#5a6a82', fontFamily:"'DM Mono',monospace" }}>Profit</span>
        </div>
      </div>

      {/* ── Month detail panel ── */}
      {selectedMonth !== null && selectedCells && (
        <MonthDetail
          month={selectedMonth}
          year={year}
          cells={selectedCells}
          maxAbs={maxAbs}
          onClose={() => setSelectedMonth(null)}
        />
      )}

      <Tooltip cell={hovered} x={mx} y={my} />
    </div>
  );
}